import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ContactAgent from './components/ContactAgent';
import { 
  Play, 
  Download, 
  Check, 
  ArrowRight,
  Plus,
  Minus
} from 'lucide-react';

function App() {
  const [propertyPrice, setPropertyPrice] = useState(1200000);
  const [downPayment, setDownPayment] = useState(200000);
  const [loanDuration, setLoanDuration] = useState(10);
  const [residencyType, setResidencyType] = useState('UAE National');

  const calculateMonthlyPayment = () => {
    const loanAmount = propertyPrice - downPayment;
    const monthlyRate = 0.0424 / 12; // 4.24% annual rate
    const numberOfPayments = loanDuration * 12;
    
    const monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return Math.round(monthlyPayment);
  };

  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const photos = [
    "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/2121121/pexels-photo-2121121.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/2029667/pexels-photo-2029667.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/2343468/pexels-photo-2343468.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    "https://images.pexels.com/photos/2343465/pexels-photo-2343465.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
  ];

  const amenities = [
    { name: "Laundry room", checked: true },
    { name: "Covered Parking", checked: true },
    { name: "Barbecue area", checked: true },
    { name: "Air conditioning", checked: true },
    { name: "Pets Allowed", checked: true },
    { name: "Carbon Monoxide Alarm", checked: true },
    { name: "Furnished", checked: true },
    { name: "Smart Home Integrations", checked: true },
    { name: "Walk in Closet", checked: true },
    { name: "Private Garden", checked: true },
    { name: "Kitchen Appliances", checked: true },
    { name: "Facilities For Disable", checked: true },
    { name: "Maids Room", checked: true },
    { name: "Broadband Internet", checked: true },
    { name: "Security Staff", checked: true }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop')`
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        {/* Navigation */}
        <motion.nav 
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 flex justify-between items-center px-4 sm:px-6 lg:px-8 py-4 lg:py-6"
        >
          <div className="text-white text-xl sm:text-2xl font-bold">
            MENA
            <span className="block text-xs sm:text-sm font-normal tracking-wider">HOMES</span>
          </div>
        </motion.nav>

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <div className="text-center text-white max-w-6xl mx-auto">
            <motion.h1 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 leading-tight"
            >
              Park Gate Residence 3,
            </motion.h1>
            <motion.p 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg sm:text-xl md:text-2xl mb-8 opacity-90"
            >
              Park Gate Residences, Al Kitaf, Dubai
            </motion.p>
            
            <motion.div 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8"
            >
              <span className="text-3xl sm:text-4xl md:text-5xl font-bold">4,250,000 AED</span>
              <span className="bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold">
                FOR SALE
              </span>
            </motion.div>

            <motion.button 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-transparent border-2 border-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300"
            >
              CONTACT AGENT
            </motion.button>
          </div>
        </div>

        {/* Property Details */}
        <motion.div 
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 w-full px-4"
        >
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-white max-w-4xl mx-auto">
            <div className="text-center min-w-0">
              <div className="text-xs sm:text-sm text-gray-300 mb-1">BEDS</div>
              <div className="text-xl sm:text-3xl font-bold">2</div>
            </div>
            <div className="text-center min-w-0">
              <div className="text-xs sm:text-sm text-gray-300 mb-1">BATHS</div>
              <div className="text-xl sm:text-3xl font-bold">4</div>
            </div>
            <div className="text-center min-w-0">
              <div className="text-xs sm:text-sm text-gray-300 mb-1">PROPERTY SIZE</div>
              <div className="text-xl sm:text-3xl font-bold">2,070 sqft</div>
            </div>
            <div className="text-center min-w-0">
              <div className="text-xs sm:text-sm text-gray-300 mb-1">PROPERTY TYPE</div>
              <div className="text-xl sm:text-3xl font-bold">Apartment</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <div className="flex justify-center items-center mb-4">
              <span className="text-4xl sm:text-5xl lg:text-6xl font-light text-gray-300">ABOUT</span>
            </div>
            
            <motion.h2 
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4"
            >
              The Palm Jumeirah
            </motion.h2>
            <p className="text-gray-600 mb-6">Dubai, United Arab Emirates</p>
            
            <motion.button 
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 mx-auto"
            >
              <Download className="w-5 h-5" />
              <span>DOWNLOAD BROCHURE</span>
            </motion.button>
          </div>

          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-4xl mx-auto mb-8 lg:mb-12"
          >
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed text-center">
              A perfect blend of contemporary and warmth. This 3 bdrm/1.75 bath, exquisite townhouse features soaring ceilings, incredible wall of windows to enjoy 
              west facing views overlooking Rich Passage and surrounding village. The home was recently enhanced with many exceptional details including a 
              private, well appointed elevator. Endless hours are to be enjoyed on your expansive, covered deck. Convenient access to all Lynwood Center has to 
              offer: Restaurants, Bakery, Movie Theatre, Trails and Beach.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {amenities.map((amenity, index) => (
              <motion.div 
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex items-center space-x-3"
              >
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700 text-sm sm:text-base">{amenity.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Videos & Floor Plans */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 lg:mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              <span className="text-gray-400">VIDEOS</span> <span className="ml-4 sm:ml-8">FLOOR PLANS</span>
            </h2>
          </motion.div>

          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative aspect-video bg-gray-800 rounded-xl lg:rounded-2xl overflow-hidden max-w-5xl mx-auto"
          >
            <img 
              src="https://images.pexels.com/photos/2343468/pexels-photo-2343468.jpeg?auto=compress&cs=tinysrgb&w=1200&h=675&fit=crop"
              alt="Property Video"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
              >
                <Play className="w-6 h-6 sm:w-8 sm:h-8 text-gray-800 ml-1" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photos */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 lg:mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8">PHOTOS</h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 lg:mb-12">
            {photos.map((photo, index) => (
              <motion.div 
                key={index}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="aspect-square bg-gray-800 rounded-lg overflow-hidden cursor-pointer"
              >
                <img 
                  src={photo}
                  alt={`Property Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center"
          >
            <button className="border-2 border-white text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300">
              VIEW MORE
            </button>
          </motion.div>
        </div>
      </section>

      {/* Mortgage Calculator */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 lg:mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">MORTGAGE CALCULATOR</h2>
            <button className="bg-blue-500 hover:bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 mx-auto">
              <span>GET STARTED</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <motion.div 
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6 lg:space-y-8"
            >
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Mortgage calculator</h3>
                <p className="text-gray-600 mb-6">Estimate what your monthly mortgage payments will be.</p>
                
                {/* Residency Type Tabs */}
                <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
                  {['UAE National', 'UAE Resident', 'Non Resident'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setResidencyType(type)}
                      className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                        residencyType === type 
                          ? 'bg-white text-gray-900 shadow-sm' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                {/* Property Price */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Priced
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        value={propertyPrice}
                        onChange={(e) => setPropertyPrice(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                      />
                      <span className="absolute right-3 top-3 text-gray-500 text-sm">AED</span>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => setPropertyPrice(prev => prev + 50000)}
                        className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setPropertyPrice(prev => Math.max(100000, prev - 50000))}
                        className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Down Payment */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Down Payments
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        value={downPayment}
                        onChange={(e) => setDownPayment(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                      />
                      <span className="absolute right-3 top-3 text-gray-500 text-sm">AED</span>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => setDownPayment(prev => prev + 25000)}
                        className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDownPayment(prev => Math.max(50000, prev - 25000))}
                        className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Loan Duration */}
                <div className="mb-6 lg:mb-8">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Loan duration
                    </label>
                    <span className="text-sm font-medium text-gray-900">
                      {loanDuration} years
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      type="range"
                      min="5"
                      max="30"
                      value={loanDuration}
                      onChange={(e) => setLoanDuration(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div 
                      className="absolute top-0 left-0 h-2 bg-blue-600 rounded-lg pointer-events-none"
                      style={{ width: `${((loanDuration - 5) / 25) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-gray-500">
                  Please note that the calculation provided is for estimation purposes only 
                  and may not reflect the final terms of your mortgage.
                </p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:pl-8"
            >
              <div className="bg-blue-50 border border-blue-200 rounded-xl lg:rounded-2xl p-6 lg:p-8">
                <div className="text-center mb-6">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-900 mb-2">
                    {formatNumber(calculateMonthlyPayment())} AED
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 uppercase tracking-wide">
                    MONTHLY COST
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm sm:text-base">Mortgage payment:</span>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base">
                      {formatNumber(propertyPrice - downPayment)} AED
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm sm:text-base">Interest rate:</span>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base">4.24%</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Agent Section */}
      <ContactAgent />
    </div>
  );
}

export default App;